import { useCallback, useEffect, useState } from 'react';
import {
  deleteDeliveryScheduleRoute,
  fetchDeliveryScheduleRoutes,
  upsertDeliveryScheduleRoute,
  type DeliveryScheduleRoute,
} from '../../../lib/applicationFormSettings';
import { useFormsSettingsRealtime } from '../../../hooks/useFormsSettingsRealtime';
import { Modal } from '../../ui/Modal';
import { AfSettingsSection } from './AfSettingsSection';

type Draft = {
  postal_prefix: string;
  territory: string;
  schedule: string;
  remarks: string;
  sort_order: number;
};

const EMPTY_DRAFT: Draft = {
  postal_prefix: '',
  territory: '',
  schedule: '',
  remarks: '',
  sort_order: 0,
};

export function ApplicationFormDeliverySchedulePanel() {
  const [rows, setRows] = useState<DeliveryScheduleRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [editRow, setEditRow] = useState<DeliveryScheduleRoute | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>(EMPTY_DRAFT);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await fetchDeliveryScheduleRoutes(false));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load delivery schedule routes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useFormsSettingsRealtime({
    tables: ['delivery_schedule_routes'],
    onChange: () => {
      void load();
    },
  });

  function openCreate() {
    setError(null);
    setMessage(null);
    setDraft({ ...EMPTY_DRAFT, sort_order: (rows.length + 1) * 10 });
    setCreateOpen(true);
  }

  function openEdit(row: DeliveryScheduleRoute) {
    setError(null);
    setMessage(null);
    setEditRow(row);
    setEditDraft({
      postal_prefix: row.postal_prefix,
      territory: row.territory,
      schedule: row.schedule,
      remarks: row.remarks,
      sort_order: row.sort_order,
    });
  }

  async function submitCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.postal_prefix.trim()) {
      setError('Postal prefix is required (first 2 digits).');
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await upsertDeliveryScheduleRoute({
        postal_prefix: draft.postal_prefix.trim(),
        territory: draft.territory.trim(),
        schedule: draft.schedule.trim(),
        remarks: draft.remarks.trim(),
        sort_order: draft.sort_order || (rows.length + 1) * 10,
        active: true,
      });
      setCreateOpen(false);
      setDraft(EMPTY_DRAFT);
      setMessage('Route added.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add route.');
    } finally {
      setSaving(false);
    }
  }

  async function submitEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editRow) return;
    if (!editDraft.postal_prefix.trim()) {
      setError('Postal prefix is required (first 2 digits).');
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await upsertDeliveryScheduleRoute({
        id: editRow.id,
        postal_prefix: editDraft.postal_prefix.trim(),
        territory: editDraft.territory.trim(),
        schedule: editDraft.schedule.trim(),
        remarks: editDraft.remarks.trim(),
        sort_order: editDraft.sort_order,
        active: editRow.active,
      });
      setEditRow(null);
      setMessage('Route updated.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update route.');
    } finally {
      setSaving(false);
    }
  }

  async function removeRow(row: DeliveryScheduleRoute) {
    if (!window.confirm(`Delete route for postal prefix ${row.postal_prefix}?`)) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await deleteDeliveryScheduleRoute(row.id);
      setMessage(`Deleted prefix ${row.postal_prefix}.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete route.');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(row: DeliveryScheduleRoute) {
    setSaving(true);
    setError(null);
    try {
      await upsertDeliveryScheduleRoute({
        id: row.id,
        postal_prefix: row.postal_prefix,
        territory: row.territory,
        schedule: row.schedule,
        remarks: row.remarks,
        sort_order: row.sort_order,
        active: !row.active,
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update route.');
    } finally {
      setSaving(false);
    }
  }

  function renderFields(
    value: Draft,
    onChange: (next: Draft) => void,
    disabled: boolean,
  ) {
    return (
      <>
        <label className="af-settings-field">
          <span>Postal prefix (2 digits)</span>
          <input
            value={value.postal_prefix}
            onChange={(e) => onChange({ ...value, postal_prefix: e.target.value })}
            placeholder="e.g. 01"
            maxLength={2}
            required
            disabled={disabled}
          />
        </label>
        <label className="af-settings-field">
          <span>Territory</span>
          <input
            value={value.territory}
            onChange={(e) => onChange({ ...value, territory: e.target.value })}
            placeholder="e.g. CITY"
            disabled={disabled}
          />
        </label>
        <label className="af-settings-field">
          <span>Delivery schedule</span>
          <input
            value={value.schedule}
            onChange={(e) => onChange({ ...value, schedule: e.target.value })}
            placeholder="e.g. 135 or MIXED"
            disabled={disabled}
          />
        </label>
        <label className="af-settings-field">
          <span>Remarks</span>
          <textarea
            rows={3}
            value={value.remarks}
            onChange={(e) => onChange({ ...value, remarks: e.target.value })}
            placeholder="Optional guideline notes for AR"
            disabled={disabled}
          />
        </label>
      </>
    );
  }

  return (
    <>
      <AfSettingsSection
        title="Delivery schedule routes"
        description="Match delivery postal code (first 2 digits) to territory and schedule when AR approves. Not shown on the customer application form. AR can still edit on review."
        badge={`${rows.filter((r) => r.active).length} active`}
        error={error && !editRow && !createOpen ? error : null}
        message={message}
      >
        {!createOpen ? (
          <div className="af-settings-section__actions" style={{ marginBottom: 12 }}>
            <button type="button" className="btn-primary btn-sm" onClick={openCreate}>
              Add route
            </button>
          </div>
        ) : (
          <form className="af-settings-add-card af-settings-add-card--top" onSubmit={(e) => void submitCreate(e)}>
            <h3 className="af-settings-add-card__title">Add route</h3>
            {renderFields(draft, setDraft, saving)}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={saving}
                onClick={() => setCreateOpen(false)}
              >
                Cancel
              </button>
              <button type="submit" className="btn-primary btn-sm" disabled={saving}>
                {saving ? 'Saving…' : 'Add'}
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <p className="af-settings-loading">Loading routes…</p>
        ) : rows.length === 0 ? (
          <p className="af-settings-table__empty">No delivery schedule routes yet.</p>
        ) : (
          <div className="af-settings-table-wrap">
            <table className="af-settings-table">
              <thead>
                <tr>
                  <th>Prefix</th>
                  <th>Territory</th>
                  <th>Schedule</th>
                  <th>Remarks</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <code className="af-settings-code">{row.postal_prefix}</code>
                    </td>
                    <td>{row.territory || '—'}</td>
                    <td>{row.schedule || '—'}</td>
                    <td style={{ maxWidth: 280, whiteSpace: 'pre-wrap', fontSize: '0.85em' }}>
                      {row.remarks || '—'}
                    </td>
                    <td>{row.active ? 'Active' : 'Off'}</td>
                    <td className="af-settings-table__actions">
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          disabled={saving}
                          onClick={() => openEdit(row)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          disabled={saving}
                          onClick={() => void toggleActive(row)}
                        >
                          {row.active ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          disabled={saving}
                          onClick={() => void removeRow(row)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AfSettingsSection>

      <Modal
        title="Edit delivery route"
        open={editRow != null}
        onClose={() => {
          if (!saving) setEditRow(null);
        }}
        wide
      >
        <form className="unified-users-create" onSubmit={(e) => void submitEdit(e)}>
          {error && editRow ? <p className="alert alert-error">{error}</p> : null}
          {renderFields(editDraft, setEditDraft, saving)}
          <div className="unified-users-create__footer">
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={saving}
              onClick={() => setEditRow(null)}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary btn-sm" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
